import {
  Component,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';

import { RegistrationsComponent } from '@dpr-details/models/component';
import {
  DprDetailsGridBaseComponent,
  DprPersonnelDetailGridData,
} from '@dpr-details/models/grid';
import { DailyStockService, WorkedHoursService } from '@dpr-details/services';
import { DprPersonnelGridService } from '@dpr-details/services/grid';
import { NumericEditComponent, TextDisplayComponent } from '@grid/components';
import {
  GridAlignment,
  GridCellContentAlignment,
  GridFooterType,
} from '@grid/enums';
import { GridColumn, GridOptions } from '@grid/models';
import { DecimalDisplayComponent } from '@shared/components';
import { PersonnelVersion, StaticMessage } from '@shared/enums';
import { DprPersonnelDetail } from '@shared/models/dpr';
import { GridColumns, GridDefaultLabels } from '@shared/utils/grid';
import { Constants, getStaticMessage } from '@shared/utils';

@UntilDestroy()
@Component({
  selector: 'app-dpr-personnel-registrations-grid',
  templateUrl: './dpr-personnel-registrations-grid.component.html',
  styleUrls: ['./dpr-personnel-registrations-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprPersonnelRegistrationsGridComponent
  extends DprDetailsGridBaseComponent<DprPersonnelDetailGridData>
  implements RegistrationsComponent<DprPersonnelDetailGridData>, OnChanges
{
  personnelVersion = PersonnelVersion.Stock;

  constructor(
    private dprPersonnelGridService: DprPersonnelGridService,
    private dailyStockService: DailyStockService,
    private workedHoursService: WorkedHoursService
  ) {
    super();
  }

  private get personnelColumns(): GridColumn<DprPersonnelDetailGridData>[] {
    switch (this.personnelVersion) {
      case PersonnelVersion.Basic:
        return this.basicVersionColumns;
      case PersonnelVersion.Stock:
        return this.stockVersionColumns;
      case PersonnelVersion.Classic:
        return this.classicVersionColumns;
      default:
        return this.stockVersionColumns;
    }
  }

  private get basicVersionColumns(): GridColumn<DprPersonnelDetailGridData>[] {
    return [
      {
        columnDef: 'previous',
        header: {
          name: getStaticMessage(StaticMessage.PreviousColumn),
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: TextDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
        footer: {
          type: GridFooterType.Sum,
        },
      },
      {
        columnDef: 'today',
        header: {
          name: getStaticMessage(StaticMessage.TodayColumn),
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: TextDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            options: {
              allowDecimals: false,
              min: Constants.NumericOptions.intMin,
              max: Constants.NumericOptions.intMax,
              step: 1,
            },
            alignment: GridCellContentAlignment.Right,
          },
        },
        onCellEdited: this.todayChanged.bind(this),
        allowSorting: false,
        allowEditing: this.canEdit,
        footer: {
          type: GridFooterType.Sum,
        },
      },
      {
        columnDef: 'total',
        header: {
          name: getStaticMessage(StaticMessage.TotalColumn),
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 80,
        display: {
          type: TextDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
        footer: {
          type: GridFooterType.Sum,
        },
        disableChangeHighlighting: true,
      },
    ];
  }

  private get stockVersionColumns(): GridColumn<DprPersonnelDetailGridData>[] {
    return [
      {
        columnDef: 'workedHours',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colHoursPrShift:Hours pr. shift`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 50,
        display: {
          type: DecimalDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            options: {
              allowDecimals: true,
              decimalPrecision: 2,
              min: 0,
              max: Constants.NumericOptions.maxPersonnelWorkedHours,
              step: 0.01,
            },
            alignment: GridCellContentAlignment.Right,
          },
        },
        onCellEdited: this.workedHoursChanged.bind(this),
        allowSorting: false,
        allowEditing: this.canEdit,
      },
      {
        columnDef: 'dailyStockDetailsTotalStartOfDay',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colPaxStartOfDay:Pax start of day`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 70,
        display: {
          type: TextDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
        footer: {
          type: GridFooterType.Sum,
        },
      },
      {
        columnDef: 'added',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colPaxSigningOn:Pax signing on`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 70,
        display: {
          type: TextDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            options: {
              allowDecimals: false,
              min: 0,
              max: Constants.NumericOptions.intMax,
              step: 1,
            },
            alignment: GridCellContentAlignment.Right,
          },
        },
        onCellEdited: this.addedChanged.bind(this),
        allowSorting: false,
        allowEditing: this.canEdit,
        footer: {
          type: GridFooterType.Sum,
        },
      },
      {
        columnDef: 'used',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colPaxSigningOff:Pax signing off`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 70,
        display: {
          type: TextDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            options: {
              allowDecimals: false,
              min: 0,
              max: Constants.NumericOptions.intMax,
              step: 1,
            },
            alignment: GridCellContentAlignment.Right,
          },
        },
        onCellEdited: this.usedChanged.bind(this),
        allowSorting: false,
        allowEditing: this.canEdit,
        footer: {
          type: GridFooterType.Sum,
        },
      },
      {
        columnDef: 'dailyStockDetailsTotalEndOfDay',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colPaxEndOfDay:Pax end of day`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 60,
        display: {
          type: TextDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
        footer: {
          type: GridFooterType.Sum,
        },
        disableChangeHighlighting: true,
      },
      {
        columnDef: 'workedHoursDetailsTotal',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colManHoursCurrentDay:Man-hours current day`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 70,
        display: {
          type: DecimalDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
        footer: {
          type: GridFooterType.Sum,
          format: '1.2-2',
        },
        disableChangeHighlighting: true,
      },
      {
        columnDef: 'workedHoursDetailsTotalToDate',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colManHoursTotalToDate:Man-hours total to date`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 70,
        display: {
          type: DecimalDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
        footer: {
          type: GridFooterType.Sum,
          format: '1.2-2',
        },
        disableChangeHighlighting: true,
      },
    ];
  }

  private get classicVersionColumns(): GridColumn<DprPersonnelDetailGridData>[] {
    return [
      {
        columnDef: 'workedHours',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colHoursPrShift:Hours pr. shift`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 50,
        display: {
          type: DecimalDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            options: {
              allowDecimals: true,
              decimalPrecision: 2,
              min: 0,
              max: Constants.NumericOptions.maxPersonnelWorkedHours,
              step: 0.01,
            },
            alignment: GridCellContentAlignment.Right,
          },
        },
        onCellEdited: this.workedHoursChanged.bind(this),
        allowSorting: false,
        allowEditing: this.canEdit,
      },
      {
        columnDef: 'currentDay',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colPaxCurrentDay:Pax current day`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 70,
        display: {
          type: TextDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            options: {
              allowDecimals: false,
              min: 0,
              max: Constants.NumericOptions.intMax,
              step: 1,
            },
            alignment: GridCellContentAlignment.Right,
          },
        },
        onCellEdited: this.currentDayChanged.bind(this),
        allowSorting: false,
        allowEditing: this.canEdit,
        footer: {
          type: GridFooterType.Sum,
        },
      },
      {
        columnDef: 'workedHoursDetailsTotal',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colManHoursCurrentDay:Man-hours current day`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 70,
        display: {
          type: DecimalDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
        footer: {
          type: GridFooterType.Sum,
          format: '1.2-2',
        },
        disableChangeHighlighting: true,
      },
      {
        columnDef: 'workedHoursDetailsTotalToDate',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colManHoursTotalToDate:Man-hours total to date`,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        minWidth: 70,
        display: {
          type: DecimalDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
        footer: {
          type: GridFooterType.Sum,
          format: '1.2-2',
        },
        disableChangeHighlighting: true,
      },
    ];
  }

  //#region Lifecycle Hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.predefinedChanges?.currentValue) {
      this.gridComponent?.api?.setPredefinedChanges?.(this.predefinedChanges);
    }

    if (changes.selectedSubproject?.currentValue && this.labeling) {
      this.personnelVersion =
        this.selectedSubproject.data?.subprojectConfiguration
          ?.personnelVersion ?? PersonnelVersion.Stock;

      this.gridColumns$.next(this.getGridColumns());
    }

    if (changes.canEdit) {
      this.gridOptions$.next(this.getGridOptions());
      this.gridColumns$.next(this.getGridColumns());
    }
  }

  //#endregion

  protected getGridOptions(): GridOptions<DprPersonnelDetailGridData> {
    return {
      sequenceConfig: {
        disabled: true,
      },
      labeling: GridDefaultLabels,
      addConfig: {
        allowAdding: false,
        keepAutogeneratedIds: true,
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      deleteConfig: {
        allowDeleting: false,
      },
      saveConfig: {
        isExternalSave: true,
      },
      rowConfig: {
        allowDeleting: false,
        allowSelection: false,
      },
      selectionConfig: {
        allowSelection: false,
      },
      sortConfig: {
        allowSorting: false,
      },
      footerConfig: {
        displayFooter: true,
      },
      events: {
        onGridRegistered: this.onGridRegistered.bind(this),
      },
    };
  }

  protected getGridColumns(): GridColumn<DprPersonnelDetailGridData>[] {
    return [
      {
        columnDef: 'name',
        header: {
          name: $localize`:@@dprDetails.dprPersonnelGrid.colEntity:Entity`,
        },
        minWidth: 200,
        display: {
          type: TextDisplayComponent,
        },
        tabIndex: -1,
        disableFocusedBackground: true,
        allowSorting: false,
        allowEditing: false,
        footer: {
          type: GridFooterType.Text,
          label: $localize`:@@dprDetails.dprPersonnelGrid.txtTotal:Total`,
        },
      },
      ...this.personnelColumns,
      {
        ...GridColumns.comment,
        minWidth: this.personnelVersion === PersonnelVersion.Stock ? 70 : 200,
      },
    ];
  }

  //#region Private methods

  private todayChanged(data: DprPersonnelDetailGridData): void {
    // edit cell is needed here to trigger the total row update beside the cell update
    this.api.editCell(
      data,
      'total',
      this.dprPersonnelGridService.calculateTotal(data.previous, data.today)
    );
  }

  private addedChanged(data: DprPersonnelDetailGridData): void {
    const updatedDailyStockDetails = this.dailyStockService.onAddedChanged(
      data,
      data.dailyStockDetails
    );

    // edit cell is needed here to trigger the total row update beside the cell update
    this.api.editCell(
      data,
      'dailyStockDetailsTotalEndOfDay',
      updatedDailyStockDetails.totalEndOfDay
    );

    this.updatedWorkedHoursDetails(data);
  }

  private usedChanged(
    data: DprPersonnelDetailGridData,
    oldValue: number
  ): void {
    const updatedDailyStockDetails = this.dailyStockService.onUsedChanged(
      data,
      data.dailyStockDetails,
      oldValue
    );

    // edit cell is needed here to trigger the total row update beside the cell update
    this.api.editCell(
      data,
      'dailyStockDetailsTotalEndOfDay',
      updatedDailyStockDetails.totalEndOfDay
    );
    this.api.editCell(
      data,
      'dailyStockDetailsTotalUsed',
      updatedDailyStockDetails.totalUsed
    );

    this.updatedWorkedHoursDetails(data);
  }

  private currentDayChanged(data: DprPersonnelDetailGridData): void {
    this.updatedWorkedHoursDetails(data);
  }

  private workedHoursChanged(data: DprPersonnelDetailGridData): void {
    this.updatedWorkedHoursDetails(data);
  }

  private updatedWorkedHoursDetails(data: DprPersonnelDetailGridData): void {
    const updatedWorkedHoursDetails =
      this.workedHoursService.computeWorkedHoursDetails(
        data as DprPersonnelDetail,
        this.selectedSubproject?.data
      );

    // edit cell is needed here to trigger the total row update beside the cell update
    this.api.editCell(
      data,
      'workedHoursDetailsTotal',
      updatedWorkedHoursDetails.total
    );
    this.api.editCell(
      data,
      'workedHoursDetailsTotalToDate',
      updatedWorkedHoursDetails.totalToDate
    );
  }

  //#endregion
}
